import { useEffect, useState } from 'react';
import { mutate } from 'swr';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { get, post, ApiError } from '@/lib/api';
import { atMs, cellLastRunMs, cellRecovered, pollStoreUntil, type StoreLike } from '@/lib/preflight-recovery';
import { formatRelative } from '@/lib/time';
import type { PreflightEntry } from './StoreEditorDrawer';

type Props = {
  storeId: string;
  logoId: string;
  cellKey: string;
  entry: PreflightEntry;
};

export function PreflightCellRow({ storeId, logoId, cellKey, entry }: Props) {
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setError(null); }, [entry.lastRun]);

  const status = (entry.status || '').toLowerCase();
  const badgeClass = status === 'pass'
    ? 'bg-success/10 text-success'
    : status === 'fail'
    ? 'bg-danger/10 text-danger'
    : 'bg-bg text-muted';
  const badgeLabel = status === 'pass' ? '✓ pass' : status === 'fail' ? '✗ fail' : status || '—';

  async function rerun() {
    setBusy(true);
    setError(null);
    // Freshness baseline from the SERVER's persisted lastRun (skew-immune),
    // read just before the run so the dropped-connection poll below can't
    // mistake an older result for this run's.
    const storeUrl = `/api/active-stores/${storeId}`;
    const baseline = await get<StoreLike>(storeUrl).catch(() => null);
    const prevMs = baseline ? cellLastRunMs(baseline, logoId, cellKey) : atMs(entry.lastRun);
    try {
      try {
        await post(`/api/team-stores/${storeId}/preflight/${encodeURIComponent(cellKey)}`, { logoId });
      } catch (e) {
        // ApiError = the server responded — a real failure, surface it.
        // Anything else = the connection dropped mid-run (Safari aborts
        // silent ~50s fetches; big logos legitimately take that long). The
        // server keeps going and persists the result — poll for it.
        if (e instanceof ApiError) throw e;
        setRecovering(true);
        const recovered = await pollStoreUntil(
          () => get<StoreLike>(storeUrl),
          (s) => cellRecovered(s, logoId, cellKey, prevMs),
          { attempts: 36 }
        );
        if (!recovered) {
          throw new Error('Connection dropped; no saved result appeared within 3 minutes. Reopen this store to check again.');
        }
      }
      await mutate(storeUrl);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
      setError(body?.message || body?.error || (e as Error).message || 'Run failed');
    } finally {
      setBusy(false);
      setRecovering(false);
    }
  }

  return (
    <div className="flex items-center gap-2 border-b border-divider px-2 py-1.5 text-[11px] last:border-b-0">
      <span className="font-mono text-[10.5px] w-28 shrink-0">{cellKey}</span>
      <Badge className={badgeClass}>{busy ? (recovering ? 'recovering…' : 'running…') : badgeLabel}</Badge>
      <span className="flex-1 truncate text-muted">
        {entry.lastRun ? formatRelative(entry.lastRun) : '—'} · {entry.runBy || '—'}
      </span>
      <Button size="sm" variant="secondary" disabled={busy} onClick={rerun}>
        Re-run
      </Button>
      {error && (
        <span className="ml-1 text-[10.5px] text-danger" title={error}>!</span>
      )}
    </div>
  );
}
